import { isPromise } from "radash";

import { getSentry } from "./sentryClient.ts";

/**
 * Custom error class for wrapping non-Error thrown values.
 * Ensures thrown values are proper Error instances with message and cause.
 *
 * @example throw new UnknownError("Something went wrong");
 */
class UnknownError extends Error {
  constructor(cause: unknown) {
    const message = isError(cause)
      ? cause.message
      : `Unknown error: ${String(cause)}`;
    super(message);
    this.name = "UnknownError";
    this.cause = cause;
  }
}

/**
 * Converts any value to an UnknownError instance.
 *
 * @param error - The value to convert
 * @returns An UnknownError wrapping the original value
 * @example const err = toUnknownError("Something broke");
 */
function toUnknownError(error: unknown): UnknownError {
  return error instanceof UnknownError ? error : new UnknownError(error);
}

/**
 * Type guard to check if a value is an Error instance.
 *
 * @param error - The value to check
 * @returns True if the value is an Error instance
 * @example if (isError(result)) { console.error(result.message); }
 */
function isError(error: unknown): error is Error {
  return error instanceof Error;
}

/**
 * Ensures a value is converted to an Error instance.
 * If already an Error, returns it as-is; otherwise wraps in UnknownError.
 *
 * @param error - The value to ensure is an Error
 * @returns A proper Error instance
 * @example const properError = ensureError(error);
 */
function ensureError(error: unknown): Error {
  return isError(error) ? error : toUnknownError(error);
}

/**
 * Wraps a function to return errors instead of throwing them.
 * Enables functional error handling without try/catch for sync and async functions.
 *
 * @param callback - The function to wrap
 * @returns Wrapped function that returns Error | Return
 * @example
 * const safeJSON = returnError(JSON.parse);
 * const result = safeJSON('invalid');
 * if (isError(result)) { console.error(result.message); }
 */
function returnError<Args extends unknown[], Return>(
  callback: (...args: Args) => Promise<Return>,
): (...args: Args) => Promise<Error | Return>;
function returnError<Args extends unknown[], Return>(
  callback: (...args: Args) => Return,
): (...args: Args) => Error | Return;
function returnError(callback: (...args: unknown[]) => unknown) {
  return (...args: unknown[]) => {
    try {
      const result = callback(...args);
      return isPromise(result) ? result.catch(ensureError) : result;
    } catch (error) {
      return ensureError(error);
    }
  };
}

/**
 * Wraps a function to throw returned errors instead of returning them.
 * Inverse of returnError(), useful when integrating with throw-based APIs.
 *
 * @param callback - The function to wrap (should return Error | Return)
 * @returns Wrapped function that throws errors and returns non-error values
 * @example
 * const safeFn = returnError(riskyFn);
 * const throwingFn = throwError(safeFn);
 * try { throwingFn(); } catch (err) { console.error(err); }
 */
function throwError<Args extends unknown[], Return>(
  callback: (...args: Args) => Promise<Return>,
): (...args: Args) => Promise<Exclude<Return, Error>>;
function throwError<Args extends unknown[], Return>(
  callback: (...args: Args) => Return,
): (...args: Args) => Exclude<Return, Error>;
function throwError(callback: (...args: unknown[]) => unknown) {
  function handleResult(result: unknown) {
    if (isError(result)) {
      throw result;
    }
    return result;
  }

  return (...args: unknown[]) => {
    const result = callback(...args);
    return isPromise(result) ? result.then(handleResult) : handleResult(result);
  };
}

function handleFloatingPromise(promise: Promise<unknown>): void {
  promise
    .then((result) => {
      if (isError(result)) {
        getSentry().captureException(result);
      }
    })
    .catch(getSentry().captureException);
}

export {
  handleFloatingPromise,
  isError,
  returnError,
  throwError,
  toUnknownError,
  UnknownError,
};
